import { parseISO, format } from 'date-fns';
import { Video } from '../types/Video';
import { SearchCategory, DateRange, SortBy } from '../contexts/SearchContext';
import videosData from '../data/initial_videos.json';

/// 検索結果の型定義
export interface SearchResults {
  videos: Video[];
  totalCount: number;
}

/// 検索オプションの型定義
export interface SearchOptions {
  keyword: string;
  category: SearchCategory;
  dateRange: DateRange;
  sortBy?: SortBy;
  limit?: number;
}

/// 動画データのフィルタリング
const filterVideos = (videos: Video[], options: SearchOptions): Video[] => {
  let filtered = [...videos];

  // キーワード検索
  if (options.keyword.trim()) {
    const keyword = options.keyword.toLowerCase();
    filtered = filtered.filter(video =>
      video.title.toLowerCase().includes(keyword) ||
      (video.description && video.description.toLowerCase().includes(keyword))
    );
  }

  // 日付範囲フィルタ (文字列比較)
  if (options.dateRange.startDate || options.dateRange.endDate) {
    filtered = filtered.filter(video => {
      try {
        const videoDateStr = video.publishedAt.substring(0, 10); // "YYYY-MM-DD"

        if (options.dateRange.startDate) {
          const startDateStr = format(options.dateRange.startDate, 'yyyy-MM-dd');
          if (videoDateStr < startDateStr) return false;
        }

        if (options.dateRange.endDate) {
          const endDateStr = format(options.dateRange.endDate, 'yyyy-MM-dd');
          if (videoDateStr > endDateStr) return false;
        }
        
        return true;
      } catch (error) {
        console.error('Date parsing error in filterVideos:', error);
        return false;
      }
    });
  }

  // ソート
  switch (options.sortBy) {
    case 'date_desc':
      filtered.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
      break;
    case 'date_asc':
      filtered.sort((a, b) => new Date(a.publishedAt).getTime() - new Date(b.publishedAt).getTime());
      break;
    case 'views_desc':
      filtered.sort((a, b) => b.viewCount - a.viewCount);
      break;
    case 'views_asc':
      filtered.sort((a, b) => a.viewCount - b.viewCount);
      break;
    case 'likes_desc':
      filtered.sort((a, b) => b.likeCount - a.likeCount);
      break;
    case 'likes_asc':
      filtered.sort((a, b) => a.likeCount - b.likeCount);
      break;
    default:
      // デフォルトは新しい順
      filtered.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
      break;
  }

  // 制限
  if (options.limit) {
    filtered = filtered.slice(0, options.limit);
  }

  return filtered;
};

/// 特定の日付の動画を取得
export const getVideosByDate = (date: Date): Video[] => {
  const dateString = format(date, 'yyyy-MM-dd');
  
  return videosData.filter(video => {
    try {
      const videoDateString = video.publishedAt.substring(0, 10);
      return videoDateString === dateString;
    } catch {
      return false;
    }
  });
};

/// 月ごとの動画投稿数を取得
export const getVideosCountByMonth = (year: number, month: number): { [day: number]: number } => {
  const counts: { [day: number]: number } = {};
  
  videosData.forEach(video => {
    try {
      // YYYY-MM-DDTHH:mm:ssZ
      const videoYear = parseInt(video.publishedAt.substring(0, 4), 10);
      const videoMonth = parseInt(video.publishedAt.substring(5, 7), 10) - 1; // Month is 0-indexed
      if (videoYear === year && videoMonth === month) {
        const day = parseInt(video.publishedAt.substring(8, 10), 10);
        counts[day] = (counts[day] || 0) + 1;
      }
    } catch (e) {
      // 日付パースエラーは無視
    }
  });
  
  return counts;
};

/// 動画の投稿日時の範囲を取得する
export const getVideoDateRange = (): { minDate: Date; maxDate: Date } | null => {
  if (videosData.length === 0) {
    return null;
  }

  let minDate = parseISO(videosData[0].publishedAt.slice(0, -1));
  let maxDate = parseISO(videosData[0].publishedAt.slice(0, -1));

  for (const video of videosData) {
    const currentDate = parseISO(video.publishedAt.slice(0, -1));
    if (currentDate < minDate) {
      minDate = currentDate;
    }
    if (currentDate > maxDate) {
      maxDate = currentDate;
    }
  }

  return { minDate, maxDate };
};

/// 検索実行
export const search = async (options: SearchOptions): Promise<SearchResults> => {
  // 非同期処理をシミュレート（実際のAPI呼び出し時に備えて）
  await new Promise(resolve => setTimeout(resolve, 100));

  let videos: Video[] = [];

  switch (options.category) {
    case 'videos':
      videos = filterVideos(videosData, options);
      break;
    case 'all':
    default:
      // 動画のみを検索対象とする
      videos = filterVideos(videosData, options);
      break;
  }

  return {
    videos,
    totalCount: videos.length
  };
};

/// 人気の検索キーワードを取得（今後の実装用）
export const getPopularKeywords = (): string[] => {
  return ['雑談', '歌枠', 'ゲーム', 'ASMR', 'お絵描き'];
};

/// 最近の検索履歴を取得（今後の実装用）
export const getRecentSearches = (): string[] => {
  const searches = localStorage.getItem('recentSearches');
  return searches ? JSON.parse(searches) : [];
};

/// 検索履歴を保存
export const saveSearchHistory = (keyword: string): void => {
  if (!keyword.trim()) return;
  
  const searches = getRecentSearches();
  const filtered = searches.filter(s => s !== keyword);
  const updated = [keyword, ...filtered].slice(0, 10); // 最大10件
  
  localStorage.setItem('recentSearches', JSON.stringify(updated));
}; 